import React from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import IntegrationActivityLog from "../IntegrationActivityLog";
import { SectionCard, PrimaryButton, SecondaryButton, BackLink, Tile, TileGrid } from "@/components/ds";
import IconByName from "@/components/ds/IconByName";
import LoadingSpinner from "@/components/LoadingSpinner";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import VelaroVoiceSetupWizard, { ProvisioningRecord } from "./VelaroVoiceSetupWizard";

const VELARO_PHONE_COLOR = "#1A56DB";

export default function SkySwitch() {
  const api = useApi();
  const [tab, setTab] = React.useState<"overview" | "activity">("overview");
  const [showWizard, setShowWizard] = React.useState(false);
  const [record, setRecord] = React.useState<ProvisioningRecord | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const fetchStatus = React.useCallback(() => {
    setLoading(true);
    setError("");
    return api.messaging
      .get("TelephonyProvisioning")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d: ProvisioningRecord) => setRecord(d))
      .catch((err) => {
        setError(`Failed to load Velaro Voice status: ${err instanceof Error ? err.message : String(err)}`);
        setRecord(null);
      })
      .finally(() => setLoading(false));
  }, [api.messaging]);

  React.useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const tabsJSX = (
    <>
      <IntegrationTab active={tab === "overview"} onClick={() => setTab("overview")}>Overview</IntegrationTab>
      <IntegrationTab active={tab === "activity"} onClick={() => setTab("activity")}>Activity Log</IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="Velaro Voice" tabs={tabsJSX}>
      {tab === "overview" && (
        <div className="space-y-4">
          <SectionCard
            title="Velaro Voice"
            action={
              !loading && (
                <PrimaryButton onClick={() => setShowWizard(true)}>
                  {record?.provisioned ? "Manage Velaro Voice" : "Set up Velaro Voice"}
                </PrimaryButton>
              )
            }
          >
            <div className="text-zinc-500 text-sm leading-relaxed mb-4">
              Get a dedicated business phone number provisioned automatically, no carrier account or
              hardware required. Velaro searches your connected CRM, service, and e-commerce integrations
              in real time and shows matching customer data in the agent sidebar before they answer.
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-800">Screen pop</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">Self-serve setup</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">Call logging</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">IVR integration</span>
            </div>

            {loading && (
              <div className="py-4">
                <LoadingSpinner minHeight={80} />
              </div>
            )}

            {!loading && error && (
              <div role="alert" className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-center justify-between gap-3">
                <span>{error}</span>
                <SecondaryButton onClick={() => fetchStatus()}>Retry</SecondaryButton>
              </div>
            )}

            {!loading && !error && record?.provisioned && (
              <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-sm text-green-900">
                <strong>Connected.</strong> Your Velaro Voice number is {record.provisionedNumber || "provisioned"}.
                Open Manage Velaro Voice above to view details or disconnect.
              </div>
            )}

            {!loading && !error && !record?.provisioned && (
              <div className="p-4 rounded-xl bg-sky-50 border border-sky-200 text-sm text-sky-900">
                <strong>Not connected yet.</strong> Click Set up Velaro Voice above to provision a dedicated
                phone number for your business in minutes. No carrier account or hardware needed,
                Velaro handles it end to end.
              </div>
            )}
          </SectionCard>

          <SectionCard title="What you get" description="All features use the same screen pop provider system — your CRM integrations are automatically available.">
            <TileGrid columns={{ base: 1, md: 2 }} gap={4}>
              {[
                { icon: "Phone", title: "Inbound call screen pop", desc: "When a call arrives at your Velaro Voice number, Velaro looks up the caller across all connected integrations and shows customer info in the agent sidebar." },
                { icon: "Building", title: "Multi-tenant / reseller support", desc: "Manage multiple businesses under one Velaro account, each with its own Velaro Voice number and configuration." },
                { icon: "FileText", title: "Post-call activity logging", desc: "Completed calls are logged as activities on the matched CRM contact — HubSpot, Salesforce, Dynamics 365, and Velaro CRM." },
                { icon: "Bot", title: "IVR hand-off with context", desc: "Calls handled by Velaro IVR can transfer to a live agent with full conversation context — agents see the transcript before connecting." },
              ].map((item) => (
                <Tile key={item.title} density="comfortable" className="items-start">
                  <Tile.Leading><span className="text-zinc-600"><IconByName name={item.icon} className="w-5 h-5" /></span></Tile.Leading>
                  <Tile.Body>
                    <Tile.Title className="font-semibold">{item.title}</Tile.Title>
                    <Tile.Description>{item.desc}</Tile.Description>
                  </Tile.Body>
                </Tile>
              ))}
            </TileGrid>
          </SectionCard>

          <SectionCard title="How screen pop works" description="Velaro Voice uses the same automatic lookup system as all Velaro phone integrations.">
            <div className="text-sm text-zinc-600 mb-4 leading-relaxed">
              Every enabled CRM and service integration on your site is checked automatically when a call arrives — HubSpot, Salesforce, Dynamics 365, ServiceTitan, ServiceNow, VAST, and more. Lookups run in parallel; the first match surfaces in the sidebar.
            </div>
            <Tile density="comfortable" className="text-xs text-zinc-500">
              See the{" "}
              <Link to="/integrations/nextiva" className="text-[var(--color-primary)] underline">
                Nextiva integration page
              </Link>{" "}
              for the full list of integrations with screen pop support and a detailed walkthrough of how the lookup works.
            </Tile>
          </SectionCard>

          <SectionCard title="Setup process">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: "1", title: "Connect", desc: "Tell us your business name, contact email, and preferred area code. No carrier account needed." },
                { step: "2", title: "Verify", desc: "Review your details before we create your account." },
                { step: "3", title: "Configure & finish", desc: "Optionally assign a starting extension, then activate. Your number is ready to take calls immediately." },
              ].map((s) => (
                <div key={s.step} className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold mt-0.5" style={{ backgroundColor: VELARO_PHONE_COLOR }}>{s.step}</span>
                  <div>
                    <div className="text-sm font-semibold text-zinc-700 mb-1">{s.title}</div>
                    <div className="text-xs text-zinc-500 leading-relaxed">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {tab === "activity" && <IntegrationActivityLog integrationSlug="VelaroPhone" />}

      {showWizard && (
        <VelaroVoiceSetupWizard
          onClose={() => setShowWizard(false)}
          onChanged={() => fetchStatus()}
        />
      )}
    </StickyRouteHeader>
  );
}

export function BackToIntegrationLandingSkySwitch() {
  return <BackLink to="/integrations" label="Integrations" />;
}
